import { useMemo } from "react";
import type { ChipTone } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import type {
  InvoiceItemBilling,
  InvoiceItemFulfillment,
  InvoiceStatus,
  RestaurantSettings,
  SettlementMethod,
} from "@/lib/api/types";
// B117/C10 — the store's own timezone, for `useInvoiceDateFormat`. Shared by
// query key with every other reader of settings, so no screen pays twice.
import { endpoints } from "@/lib/api/endpoints";
import { queryKeys, useApiQuery } from "@/shared/queries";

type Translate = ReturnType<typeof useTranslate>;

/**
 * Colour for an invoice status — always paired with a TEXT label, never used
 * alone.
 *
 * `docs/design` forbids relying on colour by itself, and the raw enum
 * (`SETTLED`) is not a label a cashier reads. Every call site therefore renders
 * `<Chip tone={INVOICE_STATUS_TONES[s]}>{invoiceStatusLabel(t, s)}</Chip>` —
 * the tone is the fast scan, the word is the meaning.
 *
 * The four labels already exist as `pos.status*`, written once for the till.
 * They are reused rather than duplicated: a second Arabic word for "settled"
 * that drifted from the first would be worse than no translation at all.
 */
export const INVOICE_STATUS_TONES: Record<InvoiceStatus, ChipTone> = {
  /*
   * One colour carries one meaning on this screen, and amber means EXCEPTION.
   *
   * OPEN was amber, on the reasoning that an open ticket is live money and the
   * POS floor paints it gold. On the floor that holds — every tile there is a
   * table, and gold means "this one is running". In a history list it did not:
   * amber sat beside CANCELLED's red as the only two coloured pills, so the
   * most ordinary state on the screen wore the same warning tint as the one
   * thing that actually went wrong.
   *
   * So: blue for live, neutral for awaiting payment, green for settled, amber
   * for cancelled — which is also the artboard's own split, where green is the
   * normal ticket and amber is the refunded one.
   */
  OPEN: "info",
  CLOSED: "neutral",
  SETTLED: "success",
  CANCELLED: "warning",
};

/**
 * A WRITE-OFF IS NOT "COLLECTED", WHATEVER THE STATUS COLUMN SAYS (D31).
 *
 * A written-off bill is `SETTLED` — it has to be, because the ticket must leave
 * the floor and a bill parked as CLOSED forever is the leak, not the fix. So
 * the status alone cannot name it: the history list printed the same green
 * «متحصّلة» on a walk-out as on the cash bill above it, while the tile at the
 * top of the same screen was carefully excluding it from Sales and the payment
 * column beside it said «من غير دفع». One row, three different answers.
 *
 * D31's rule is that a write-off is "surfaced separately everywhere it counts
 * zero", and a status chip is one of those places. It reuses `pos.writeOff` —
 * the word the settle sheet, the Z and the shift review already use — rather
 * than inventing a fifth status: this is still SETTLED, it just did not settle
 * in money.
 *
 * Both helpers take the method OPTIONALLY, so a call site that genuinely only
 * has a status (the filter dropdown, which lists statuses and not methods)
 * keeps the old behaviour by passing nothing.
 */
function isWrittenOff(
  status: InvoiceStatus,
  settlementMethod?: SettlementMethod | null,
): boolean {
  return status === "SETTLED" && settlementMethod === "WRITE_OFF";
}

export function invoiceStatusLabel(
  t: Translate,
  status: InvoiceStatus,
  settlementMethod?: SettlementMethod | null,
): string {
  if (isWrittenOff(status, settlementMethod)) {
    return t((m) => m.pos.writeOff);
  }
  switch (status) {
    case "OPEN":
      return t((m) => m.pos.statusOpen);
    case "CLOSED":
      return t((m) => m.pos.statusClosed);
    case "SETTLED":
      return t((m) => m.pos.statusSettled);
    case "CANCELLED":
      return t((m) => m.pos.statusCancelled);
  }
}

/**
 * The chip's colour, with the same exception.
 *
 * `warning`, not `success`: amber is this screen's EXCEPTION colour (see the
 * tone map's own note), and a walk-out belongs with the cancelled ticket rather
 * than with the money that arrived. Never the only thing carrying the meaning —
 * the label above says the word.
 */
export function invoiceStatusTone(
  status: InvoiceStatus,
  settlementMethod?: SettlementMethod | null,
): ChipTone {
  return isWrittenOff(status, settlementMethod)
    ? "warning"
    : INVOICE_STATUS_TONES[status];
}

/**
 * How a bill was paid (D31), in words — RE-EXPORTED, not implemented here.
 *
 * The implementation moved to `@/lib/settlementMethodLabel` because the TILL
 * names a settlement method too (the settled overlay and the customer's
 * receipt), and this module is back-office-only. Two copies of the rule is how
 * `INSTAPAY` came to print as a raw enum token on a customer's tax receipt while
 * the drawer tile beside it said «تحويلات إنستاباي» — see that file.
 *
 * Kept as an export from here so the back-office call sites (history rows and
 * their method filter, the detail card, the correction picker, the shift-
 * exception lines) keep one import, and so this is the only place the redirect
 * has to be read to be understood.
 */
export {
  settlementMethodLabel,
  // B117/B1 — its plural, for the bill detail and the history row. Re-exported
  // beside it for the reason stated above: the back-office call sites keep one
  // import, and the redirect is read in one place.
  tenderMethodsLabel,
} from "@/lib/settlementMethodLabel";

/**
 * Where ONE plate is, in the kitchen (D16).
 *
 * Distinct from the invoice's own status on purpose: a settled invoice can
 * still carry a voided plate. And distinct from BILLING, which is the other
 * axis — a comped plate reads "served" here and "on the house" there, because
 * those are two different facts and a single label could only ever tell you
 * one of them.
 */
export function invoiceItemFulfillmentLabel(
  t: Translate,
  state: InvoiceItemFulfillment,
): string {
  switch (state) {
    case "PENDING":
      return t((m) => m.invoices.itemPending);
    case "DRAFT":
      return t((m) => m.invoices.itemDraft);
    case "QUEUED":
      return t((m) => m.invoices.itemQueued);
    case "PREPARING":
      return t((m) => m.invoices.itemPreparing);
    case "READY":
      return t((m) => m.invoices.itemReady);
    case "SERVED":
      return t((m) => m.invoices.itemServed);
    case "VOIDED":
      return t((m) => m.invoices.itemVoided);
  }
}

/** What the customer pays for this plate (D16). */
export function invoiceItemBillingLabel(
  t: Translate,
  billing: InvoiceItemBilling,
): string {
  switch (billing) {
    case "CHARGED":
      return t((m) => m.invoices.itemActive);
    case "COMPED":
      return t((m) => m.invoices.itemComped);
    case "NOT_CHARGED":
      return t((m) => m.invoices.itemNotCharged);
  }
}

/*
 * Amber means EXCEPTION on this screen, and the kitchen states are not
 * exceptions — they are the ordinary life of a plate. So the forward path
 * walks neutral -> info -> success as the food gets closer to the table, and
 * only the two states that mean "something went differently" take a colour
 * that stops the eye.
 */
export const INVOICE_ITEM_FULFILLMENT_TONES: Record<
  InvoiceItemFulfillment,
  ChipTone
> = {
  PENDING: "warning",
  DRAFT: "neutral",
  QUEUED: "neutral",
  PREPARING: "info",
  READY: "info",
  SERVED: "success",
  VOIDED: "danger",
};

export const INVOICE_ITEM_BILLING_TONES: Record<InvoiceItemBilling, ChipTone> = {
  CHARGED: "neutral",
  COMPED: "accent",
  NOT_CHARGED: "danger",
};

const pad = (value: number): string => String(value).padStart(2, "0");

/**
 * Today as `YYYY-MM-DD` in the DEVICE's calendar, not UTC.
 *
 * `DateRangePicker` disables days after `todayIso`. Egypt is UTC+2/+3, so
 * handing it a UTC day would grey out the day staff are actually working for
 * the first two or three hours after local midnight — precisely the shift that
 * needs to look at its own invoices.
 *
 * This is the DEVICE day, which is still not the STORE's business day (that is
 * resolved server-side from `dayStartHour`, D7). The gap only ever makes the
 * picker slightly more permissive than the business calendar, never less.
 */
export function deviceTodayIso(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * The calendar day BEFORE an instant, in the DEVICE's zone, as `YYYY-MM-DD`.
 *
 * Written for the shift screen's "you inherited these bills" link (D24), whose
 * range has to start EARLIER than the shift opened: the business day rolls at
 * `dayStartHour` (D7), so a shift opened at 18:00 can legitimately have
 * inherited a bill stamped with yesterday's business date, and a range that
 * started on the open date would hide exactly the bill the link exists to
 * find. One day back is deliberately generous rather than exact — the client
 * cannot resolve the store's business calendar, and a range that is slightly
 * too WIDE shows a few extra settled rows, while one that is too narrow shows
 * nothing and reads as "you inherited nothing".
 *
 * Returns `null` for an unparseable instant, so the caller can fall back to a
 * range with no lower bound rather than to `NaN-NaN-NaN`.
 */
export function deviceDayBeforeIso(instant: string | Date): string | null {
  const at = instant instanceof Date ? new Date(instant) : new Date(instant);
  if (Number.isNaN(at.getTime())) return null;
  at.setDate(at.getDate() - 1);
  return deviceTodayIso(at);
}

/**
 * Locale-aware formatters for the two different kinds of date on an invoice.
 *
 * A **business date** is a calendar day (`YYYY-MM-DD`) and must be formatted in
 * UTC — parsing it in the device's zone and formatting it locally shifts it a
 * day backwards for anyone west of Greenwich, which would silently mislabel
 * every row.
 *
 * A **timestamp** (`openedAt`, `closedAt`, `settledAt`) is an instant, and it is
 * shown on the STORE'S clock — not the device's (B117/C10).
 *
 * "When did this close" is a question about the clock on the wall, and the wall
 * is the restaurant's. The device's zone was a stand-in for it that happens to
 * be right when the manager is standing in the building and wrong every other
 * time: a laptop at home on holiday, a browser left on UTC, an accountant in
 * another country. Worse, it disagreed with the product's own export — one
 * shift open read `8:42 ص` on the Z and `2026-09-16 11:42` in the invoice CSV,
 * because the CSV has always been written in the store's zone, which is also
 * the zone `dayStartHour` and every business-day boundary are computed in.
 *
 * So there is one clock now, and it is the one the business day is cut on.
 * `businessDate` below is unchanged and stays UTC-parsed: a `date` column is a
 * calendar day, not an instant, and re-zoning it would shift it backwards a
 * day for anyone west of Greenwich.
 *
 * Falls back to the device's zone (`timeZone: undefined`) until the settings
 * read lands, and for a store that has none. That is the OLD behaviour, so a
 * screen rendering before the fetch resolves is no worse than it ever was —
 * and the query is shared by key with every other reader of settings, so this
 * adds no request to a screen that already asks.
 */
export function useInvoiceDateFormat() {
  const { locale } = useI18n();
  const { data: settings } = useApiQuery<RestaurantSettings>(
    queryKeys.settings,
    endpoints.restaurant.settings,
  );
  const timeZone = settings?.timezone || undefined;
  /*
   * `-u-nu-latn` — Western digits, on purpose and everywhere.
   *
   * Bare `ar-EG` renders Arabic-Indic numerals (٢:٤٨ م, ٢٨ أغسطس ٢٠٢٦), and
   * `useMoney` had already moved money to Western digits for the reason a till
   * exists: its figures are compared against a card terminal, a printed
   * receipt and a cash drawer, none of which show Arabic-Indic. Leaving the
   * clock and the date behind put two numbering systems on the same row —
   * `#12` beside `٢:٤٨` — which reads as two different products. The month
   * NAME still comes from `ar-EG`; only the digits change.
   */
  const tag = locale === "ar" ? "ar-EG-u-nu-latn" : "en-EG";
  return useMemo(() => {
    const day = new Intl.DateTimeFormat(tag, {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
    const stamp = new Intl.DateTimeFormat(tag, {
      dateStyle: "short",
      timeStyle: "short",
      timeZone,
    });
    // Clock only. A history ROW is already grouped under a business day, so
    // repeating the date on every line spends the column's width restating
    // what the filter above it already said — and in Arabic it rendered as a
    // full "٢٠٢٦/٨/٢٨، ٢:٤٨ م" where the eye wanted "٢:٤٨".
    const clock = new Intl.DateTimeFormat(tag, { timeStyle: "short", timeZone });
    // The `day` style read in the STORE's zone — an instant's calendar day,
    // worded like a business date (phase 12, PO-12: one date format per list).
    const zonedDay = new Intl.DateTimeFormat(tag, {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone,
    });
    return {
      businessDate: (value: string): string =>
        day.format(new Date(`${value}T00:00:00.000Z`)),
      /** `null` in, `null` out — an unclosed invoice has no closing time. */
      timestamp: (value: string | null): string | null =>
        value === null ? null : stamp.format(new Date(value)),
      /** Just the time of day, for a row already scoped to one business day. */
      time: (value: string): string => clock.format(new Date(value)),
      /** An instant's calendar day in the store's zone, in `businessDate`'s words. */
      day: (value: string): string => zonedDay.format(new Date(value)),
      /**
       * WHICH CLOCK THESE WERE FORMATTED ON (B117/RT-E), or `null` when the
       * store's zone was not known and the device's was used instead.
       *
       * Published because a PRINTED document has to name its clock: the
       * accountant's objection at the round table was that the Z, the CSV and
       * the screen are three documents with nothing proving they describe the
       * same day — and worse, that two prints of one Z could silently carry two
       * different clocks, because this hook falls back to the device before the
       * settings read lands. A reader cannot be expected to know which they are
       * holding, so the paper says.
       */
      timeZone: timeZone ?? null,
      /**
       * The same clock in WORDS (phase 12, PO-14): «القاهرة» / "Cairo", not
       * the IANA id «Africa/Cairo» inside Arabic prose. `null` with `timeZone`.
       */
      timeZoneName: timeZone ? timeZoneLabel(timeZone, locale) : null,
    };
  }, [tag, timeZone, locale]);
}

/**
 * A store's IANA zone as a reader names it (phase 12, PO-14). The product is
 * Egyptian, so Cairo is spelled out in both languages; any other zone falls
 * back to its city part, underscores as spaces («America/New_York» → "New
 * York") — never a raw id with a slash in a printed sentence.
 */
export function timeZoneLabel(timeZone: string, locale: string): string {
  const KNOWN: Record<string, { ar: string; en: string }> = {
    "Africa/Cairo": { ar: "القاهرة", en: "Cairo" },
  };
  const known = KNOWN[timeZone];
  if (known) return locale === "ar" ? known.ar : known.en;
  const city = timeZone.split("/").pop() ?? timeZone;
  return city.replace(/_/g, " ");
}
